/* AnnuaireEntitySelector — composant réutilisable
 *
 * Multi-sélection d'entités Annuaire (FK ontologique cross-modules).
 * Utilisé dans Négo, Pipeline, Courtage, Risk pour brancher
 * `annuaire_entity_ids: string[]` sur les modules opérationnels.
 *
 * Props :
 *   value         : string[] — ids actuels
 *   onChange      : (string[]) => void
 *   testid        : préfixe pour data-testid (ex: "neg-entity-selector")
 *   compact       : booléen — réduit la hauteur (utile dans les formulaires)
 *   placeholder   : texte du champ recherche
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { X, Search, Building2, User, Landmark, Coins, Users2, Heart, ShieldCheck, Globe2, Briefcase } from "lucide-react";

const TYPE_ICONS = {
  holding: Building2,
  societe: Briefcase,
  personne: User,
  institution: Landmark,
  fonds: Coins,
  famille: Users2,
  etat: ShieldCheck,
  ong: Heart,
};

let CACHED_ENTITIES = null;

async function fetchEntities() {
  if (CACHED_ENTITIES) return CACHED_ENTITIES;
  try {
    const r = await api.get("/workspace/annuaire/entities");
    CACHED_ENTITIES = r.data?.items || [];
    return CACHED_ENTITIES;
  } catch {
    return [];
  }
}

export function invalidateEntityCache() {
  CACHED_ENTITIES = null;
}

export default function AnnuaireEntitySelector({
  value = [],
  onChange,
  testid = "annuaire-selector",
  compact = false,
  placeholder = "Rechercher dans l'Annuaire…",
  label = "Entités liées (Annuaire)",
}) {
  const [entities, setEntities] = useState([]);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    fetchEntities().then(setEntities);
  }, []);

  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const byId = useMemo(() => Object.fromEntries(entities.map((e) => [e.id, e])), [entities]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const selected = new Set(value);
    let list = entities.filter((e) => !selected.has(e.id));
    if (q) {
      list = list.filter((e) => {
        const hay = `${e.name} ${e.type} ${e.sector || ""} ${e.country || ""}`.toLowerCase();
        return hay.includes(q);
      });
    }
    return list.slice(0, 12);
  }, [entities, value, query]);

  const add = (id) => {
    if (value.includes(id)) return;
    onChange?.([...value, id]);
    setQuery("");
  };

  const remove = (id) => {
    onChange?.(value.filter((v) => v !== id));
  };

  return (
    <div className="space-y-1.5" ref={boxRef} data-testid={testid}>
      {label && (
        <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      )}

      {/* Selected chips */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1" data-testid={`${testid}-chips`}>
          {value.map((id) => {
            const e = byId[id];
            const Icon = e ? (TYPE_ICONS[e.type] || Globe2) : Globe2;
            return (
              <span
                key={id}
                className="inline-flex items-center gap-1.5 text-[11px] bg-[#378ADD]/15 border border-[#378ADD]/40 text-[#B5D4F4] rounded-md pl-2 pr-1 py-0.5"
                data-testid={`${testid}-chip-${id}`}
              >
                <Icon className="w-3 h-3" />
                <span className="max-w-[160px] truncate">{e?.name || id.slice(0, 8)}</span>
                <button
                  type="button"
                  onClick={() => remove(id)}
                  className="hover:text-white p-0.5"
                  data-testid={`${testid}-remove-${id}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}

      {/* Search box */}
      <div className="relative">
        <div className="flex items-center gap-2 bg-black/30 border border-white/10 rounded-md px-2 py-1.5 focus-within:border-[#378ADD]/60">
          <Search className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
          <input
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder={placeholder}
            className={`bg-transparent border-0 outline-none text-xs w-full text-zinc-200 placeholder:text-zinc-600 ${compact ? "" : "py-0.5"}`}
            data-testid={`${testid}-input`}
          />
        </div>

        {open && filtered.length > 0 && (
          <div
            className="absolute z-50 mt-1 w-full max-h-60 overflow-auto rounded-md border border-white/10 bg-[#0E0E10] shadow-xl"
            data-testid={`${testid}-dropdown`}
          >
            {filtered.map((e) => {
              const Icon = TYPE_ICONS[e.type] || Globe2;
              return (
                <button
                  type="button"
                  key={e.id}
                  onClick={() => add(e.id)}
                  className="w-full text-left px-2.5 py-1.5 hover:bg-white/5 flex items-center gap-2 border-b border-white/5 last:border-0"
                  data-testid={`${testid}-option-${e.id}`}
                >
                  <Icon className="w-3 h-3 text-[#378ADD] shrink-0" />
                  <span className="text-[12px] text-zinc-200 truncate flex-1">{e.name}</span>
                  <span className="text-[9px] uppercase tracking-wider text-zinc-500">{e.type}</span>
                </button>
              );
            })}
          </div>
        )}
        {open && query && filtered.length === 0 && (
          <div className="absolute z-50 mt-1 w-full rounded-md border border-white/10 bg-[#0E0E10] px-3 py-2 text-[11px] text-zinc-500">
            Aucune entité trouvée.
          </div>
        )}
      </div>
    </div>
  );
}
